﻿import { DiscussionContext } from "@cnx/common";
import { Box, Collapse, Flex, Text } from "@mantine/core";
import {
  IconAmbulance,
  IconChevronDown,
  IconChevronUp,
  IconFileDollar,
  IconMail,
  IconMapPin,
  IconRoute,
  IconTemperature,
  IconTruck,
  IconTruckDelivery,
  IconUser,
} from "@tabler/icons-react";
import clsx from "clsx";
import IconLoadingAddress from "icons/shipment/loading-address.svg";
import IconTruckTrailer from "icons/shipment/truck-body.svg";
import IconUnloadingAddress from "icons/shipment/unloading-address.svg";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import DateFormat from "types/DateFormat";
import { dateToUTC } from "utils/date";

const ChatContextPopover = ({
  discussionContext,
}: {
  discussionContext: DiscussionContext;
}) => {
  const { t } = useTranslation(["discussions", "tripActivity"]);
  const [opened, setOpened] = useState(false);

  const {
    activityKind,
    time,
    trailer,
    truck,
    driver,
    locationName,
    locationZipCode,
    locationCityName,
    countryCode,
    locationAddress,
    tripId,
    receiver,
    receiverName,
    senderName,
    sender,
    mailId,
    bookingId,
    temperatureMax,
    temperatureMin,
  } = discussionContext || {};
  if (!discussionContext) {
    return <Box></Box>;
  }

  return (
    <Box className="w-full">
      <Box
        component="button"
        type="button"
        aria-expanded={opened}
        onClick={() => setOpened(o => !o)}
        className={clsx("w-full h-[58px] bg-gray-dark cursor-pointer", {
          "rounded-t-lg": opened,
          "rounded-lg": !opened,
        })}
      >
        <Box className="flex items-center px-4 w-full">
          {activityKind === "Purku" ? (
            <Box className="flex items-center gap-2 shrink-0">
              <Box className="h-8 w-8 bg-white flex items-center justify-center rounded-full shrink-0">
                <IconTruckDelivery color="var(--color-red-dark)" />
              </Box>
              <Box className="flex flex-row min-w-0 ml-3.5 gap-1">
                <Text fz={14} fw={600} className="truncate text-white">
                  {t("unloading")}
                </Text>
                {time && (
                  <Text fz={14} fw={400} className="truncate text-white">
                    {dateToUTC(time).format(DateFormat.DEFAULT)}
                  </Text>
                )}
              </Box>
            </Box>
          ) : activityKind === "Lastaus" ? (
            <Box className="flex items-center gap-2 shrink-0">
              <Box className="h-8 w-8 bg-white flex items-center justify-center rounded-full shrink-0">
                <IconAmbulance color="var(--color-green)" />
              </Box>
              <Box className="flex flex-row min-w-0 ml-3.5 gap-1">
                <Text fz={14} fw={600} className="truncate text-white">
                  {t("loading")}
                </Text>
                {time && (
                  <Text fz={14} fw={400} className="truncate text-white">
                    {dateToUTC(time).format(DateFormat.DEFAULT)}
                  </Text>
                )}
              </Box>
            </Box>
          ) : bookingId ? (
            <Box className="flex items-center gap-2 shrink-0">
              <Box className="h-8 w-8 bg-white flex items-center justify-center rounded-full shrink-0">
                <IconFileDollar
                  height={24}
                  width={24}
                  color="var(--color-gray-dark)"
                />
              </Box>
              <Box className="flex flex-row min-w-0 ml-3.5 gap-1">
                <Text fz={14} fw={600} className="truncate text-white">
                  {t("bookingId")}
                </Text>
                <Text fz={14} fw={400} className="truncate text-white">
                  {bookingId}
                </Text>
              </Box>
            </Box>
          ) : (
            <Box className="flex items-center gap-2 shrink-0">
              <Box className="h-8 w-8 bg-white flex items-center justify-center rounded-full shrink-0">
                <IconRoute
                  height={24}
                  width={24}
                  color="var(--color-gray-dark)"
                  className="shrink-0"
                />
              </Box>
              <Box className="flex flex-row min-w-0 ml-3.5 gap-1">
                <Text fz={14} fw={600} className="truncate text-white">
                  {t("transport")}
                </Text>
                {tripId && (
                  <Text fz={14} fw={400} className="truncate text-white">
                    {tripId}
                  </Text>
                )}
              </Box>
            </Box>
          )}
          <Box className="ml-auto shrink-0">
            {opened ? (
              <IconChevronUp color="white" size={24} />
            ) : (
              <IconChevronDown color="white" size={24} />
            )}
          </Box>
        </Box>
      </Box>
      <Collapse expanded={opened}>
        <Box
          style={{
            borderRadius: "0 0 8px 8px",
            backgroundColor: "var(--color-gray-dark)",
            padding: "10px 16px",
          }}
        >
          <Flex direction="column" justify="left" gap="10px">
            {(trailer || truck) && (
              <Box className="flex flex-row items-start gap-[60px]">
                {trailer && (
                  <Box className="flex flex-row gap-2 items-center">
                    <IconTruckTrailer
                      height={24}
                      width={24}
                      color="white"
                      className="shrink-0"
                    />
                    <Text fz={14} c={"white"}>
                      {trailer}
                    </Text>
                  </Box>
                )}
                {truck && (
                  <Box className="flex flex-row gap-2 items-center">
                    <IconTruck
                      height={24}
                      width={24}
                      color="white"
                      className="shrink-0"
                    />
                    <Text fz={14} c={"white"}>
                      {truck}
                    </Text>
                  </Box>
                )}
              </Box>
            )}
            {driver && (
              <Box className="flex flex-col w-full">
                <Box className="flex flex-row gap-2 items-start w-full">
                  <IconUser
                    height={24}
                    width={24}
                    color="white"
                    className="shrink-0"
                  />
                  <Box className="flex flex-col min-w-0">
                    <Text fz={14} c={"white"} className="wrap-break-word">
                      {driver.split(";").join(", ")}
                    </Text>
                  </Box>
                </Box>
              </Box>
            )}
            {locationName && (
              <Box className="flex flex-row gap-2 items-start">
                <IconMapPin
                  height={24}
                  width={24}
                  color="white"
                  className="shrink-0"
                />
                <Box className="flex flex-col flex-wrap">
                  <Text fz={14} c={"white"} className="w-full">
                    {locationName}
                  </Text>
                  <Text fz={14} c={"white"} className="w-full">
                    {locationAddress}
                  </Text>
                  <Text fz={14} c={"white"} className="w-full">
                    {locationZipCode} {locationCityName} {countryCode}
                  </Text>
                </Box>
              </Box>
            )}
            {tripId && (
              <Box className="flex flex-row gap-2 items-center">
                <IconRoute
                  height={24}
                  width={24}
                  color="white"
                  className="shrink-0"
                />
                <Text fz={14} c={"white"}>
                  {tripId}
                </Text>
              </Box>
            )}
            {(receiver || receiverName) && (
              <Box className="flex flex-col w-full">
                <Box className="flex flex-row gap-2 items-start w-full">
                  <IconUnloadingAddress
                    height={24}
                    width={24}
                    color="var(--color-red-dark)"
                    className="shrink-0"
                  />
                  <Box className="flex flex-col min-w-0">
                    {receiver && (
                      <Text fz={14} c={"white"} className="wrap-break-word">
                        {receiver}
                      </Text>
                    )}
                    {receiverName && (
                      <Text
                        fz={12}
                        c="#C4C4C4"
                        className={clsx("wrap-break-word", {
                          "pt-1": !receiver,
                        })}
                      >
                        Receiver: {receiverName}
                      </Text>
                    )}
                  </Box>
                </Box>
              </Box>
            )}
            {(sender || senderName) && (
              <Box className="flex flex-col w-full">
                <Box className="flex flex-row gap-2 items-start w-full">
                  <IconLoadingAddress
                    height={24}
                    width={24}
                    color="var(--color-green)"
                    className="shrink-0"
                  />
                  <Box className="flex flex-col min-w-0">
                    {sender && (
                      <Text fz={14} c={"white"} className="wrap-break-word">
                        {sender}
                      </Text>
                    )}
                    {senderName && (
                      <Text
                        fz={12}
                        c="#C4C4C4"
                        className={clsx("wrap-break-word", { "pt-1": !sender })}
                      >
                        Sender: {senderName}
                      </Text>
                    )}
                  </Box>
                </Box>
              </Box>
            )}
            {mailId && (
              <Box className="flex flex-row gap-2 items-center">
                <IconMail
                  height={24}
                  width={24}
                  color="white"
                  className="shrink-0"
                />
                <Text fz={14} c={"white"}>
                  {mailId}
                </Text>
              </Box>
            )}
            {(temperatureMax || temperatureMin) && (
              <Box className="flex flex-row gap-2 items-center">
                <IconTemperature
                  height={24}
                  width={24}
                  color="white"
                  className="shrink-0"
                />
                <Text fz={14} c={"white"}>
                  {typeof temperatureMin === "number"
                    ? temperatureMin >= 0
                      ? "+" + temperatureMin
                      : temperatureMin
                    : temperatureMin && !isNaN(Number(temperatureMin))
                    ? Number(temperatureMin) >= 0
                      ? "+" + temperatureMin
                      : temperatureMin
                    : "-"}
                  {"/"}
                  {typeof temperatureMax === "number"
                    ? temperatureMax >= 0
                      ? "+" + temperatureMax
                      : temperatureMax
                    : temperatureMax && !isNaN(Number(temperatureMax))
                    ? Number(temperatureMax) >= 0
                      ? "+" + temperatureMax
                      : temperatureMax
                    : "-"}
                  {" °C"}
                </Text>
              </Box>
            )}
          </Flex>
        </Box>
      </Collapse>
    </Box>
  );
};

export default ChatContextPopover;
